import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input, OnDestroy,
  output,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { NgControl, ReactiveFormsModule } from '@angular/forms';
import { ControlErrorDirective, InputClearButtonDirective, RequiredDirective } from 'eurogard-material-form-system';
import { RXAP_FORM_DEFINITION, RxapFormsModule } from 'eurogard-forms';
import { FormComponentProviders, FormProviders } from './form.providers';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AutocompleteOptionsFormMethodDirective } from 'autocomplete-table-select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { AutocompleteItem } from './types';
import { AutocompleteForm } from './autocomplete.form';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { SearchFilterItem } from 'angular-forms/search';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  standalone: true,
  selector: 'eurogard-autocomplete-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './autocomplete-form.component.html',
  styleUrls: [ './autocomplete-form.component.scss' ],
  imports: [
    ReactiveFormsModule,
    RxapFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    AutocompleteOptionsFormMethodDirective,
    MatIconModule,
    MatButtonModule,
    InputClearButtonDirective,
    RequiredDirective,
    ControlErrorDirective,
    MatChipGrid,
    MatChipRemove,
    MatChipRow,
    MatChipInput,
    MatTooltip,
  ],
  providers: [
    FormProviders,
    FormComponentProviders,
    {
      provide: MatFormField,
      useValue: null,
    },
    {
      provide: NgControl,
      useValue: null,
    },
  ],
})
export class AutocompleteFormComponent implements AfterViewInit, OnDestroy {
  @ViewChild('autocompleteInput', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  label = input($localize`Name`);
  placeholder = input($localize`Enter a name`);
  disabled = input(false);
  useChips = input(false);
  selectedItems = input<SearchFilterItem[]>([]);

  private inputWidth = signal(0);
  protected readonly maxNumberOfDisplayedChips = computed(() => {
    return Math.max(Math.floor((this.inputWidth() - 70) / this.CHIP_WIDTH_IN_PX), 1);
  });

  protected readonly CHIP_WIDTH_IN_PX = 135;

  private resizeObserver?: ResizeObserver;

  protected readonly displayedSelectedOptions: Signal<SearchFilterItem[]> = computed(() => {
    const selectedOptions = this.selectedItems();
    const maxNumberOfDisplayedChips = this.maxNumberOfDisplayedChips();
    if (selectedOptions.length > maxNumberOfDisplayedChips) {
      return selectedOptions.slice(0, maxNumberOfDisplayedChips - 1);
    } else {
      return selectedOptions;
    }
  });

  protected readonly remainingSelectedOptionsNames: Signal<string[]> = computed(() => {
    const selectedOptions = this.selectedItems();
    const maxNumberOfDisplayedChips = this.maxNumberOfDisplayedChips();
    if (selectedOptions.length > maxNumberOfDisplayedChips) {
      return selectedOptions.slice(maxNumberOfDisplayedChips - 1, selectedOptions.length).map(option => option.name);
    } else {
      return [];
    }
  });

  item = output<AutocompleteItem>();
  remove = output<string>();

  form = inject<AutocompleteForm>(RXAP_FORM_DEFINITION);

  protected readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.form.rxapFormGroup.disable();
      } else {
        this.form.rxapFormGroup.enable();
      }
    });
  }

  ngAfterViewInit() {
    if (this.useChips()) {
      this.inputWidth.set(this.inputRef.nativeElement.offsetWidth);

      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          this.inputWidth.set(Math.round(width));
        }
      });

      this.resizeObserver.observe(this.inputRef.nativeElement);
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    if (typeof event.option.value === 'object') {
      this.item.emit({ ...event.option.value, display:event.option.viewValue });
    } else {
      this.item.emit({ uuid: event.option.value, display: event.option.viewValue });
    }
    this.form.rxapFormGroup.reset();
  }

  removeItem(uuid: string) {
    this.remove.emit(uuid);
  }

}
